import type { Locale } from './data/schema'

export const SHARE_CARD_SIZE = 1200

export interface GraphShareCardOptions {
  graph: Blob
  locale: Locale
  title: string
  context: string
  nodeCount: number
  edgeCount: number
}

export type GraphShareResult = 'native' | 'download' | 'cancelled'

function loadBlobImage(blob: Blob) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const url = URL.createObjectURL(blob)
    const image = new Image()
    image.onload = () => {
      URL.revokeObjectURL(url)
      resolve(image)
    }
    image.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('The graph image could not be prepared.'))
    }
    image.src = url
  })
}

function canvasBlob(canvas: HTMLCanvasElement) {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('The share card could not be created.')), 'image/png')
  })
}

function fitTitle(context: CanvasRenderingContext2D, title: string, maxWidth: number) {
  let size = 50
  while (size > 34) {
    context.font = `520 ${size}px "Newsreader Variable", serif`
    if (context.measureText(title).width <= maxWidth) break
    size -= 2
  }
  return size
}

function drawBrandMark(context: CanvasRenderingContext2D, x: number, y: number) {
  context.save()
  context.translate(x, y)
  context.rotate(Math.PI / 4)
  context.strokeStyle = '#D6A647'
  context.lineWidth = 2
  context.strokeRect(-25, -25, 50, 50)
  context.globalAlpha = 0.38
  context.strokeRect(-18, -18, 36, 36)
  context.restore()
  context.globalAlpha = 1
  context.fillStyle = '#F0D28C'
  context.font = '520 31px "Newsreader Variable", serif'
  context.textAlign = 'center'
  context.textBaseline = 'middle'
  context.fillText('M', x, y + 1)
}

function drawAtmosphere(context: CanvasRenderingContext2D) {
  const glow = context.createRadialGradient(880, 230, 10, 880, 230, 620)
  glow.addColorStop(0, 'rgba(77,143,204,.16)')
  glow.addColorStop(0.55, 'rgba(42,79,111,.06)')
  glow.addColorStop(1, 'rgba(8,17,28,0)')
  context.fillStyle = glow
  context.fillRect(0, 0, SHARE_CARD_SIZE, SHARE_CARD_SIZE)

  context.save()
  context.strokeStyle = 'rgba(214,166,71,.08)'
  context.lineWidth = 1
  ;[340, 470, 610].forEach((radius) => {
    context.beginPath()
    context.arc(600, 650, radius, Math.PI * 1.08, Math.PI * 1.92)
    context.stroke()
  })
  for (let index = 0; index < 44; index += 1) {
    const x = 42 + ((index * 181) % 1110)
    const y = 38 + ((index * index * 47) % 1118)
    const radius = index % 7 === 0 ? 1.8 : 0.9
    context.fillStyle = index % 5 === 0 ? 'rgba(240,210,140,.32)' : 'rgba(244,240,232,.18)'
    context.beginPath()
    context.arc(x, y, radius, 0, Math.PI * 2)
    context.fill()
  }
  context.restore()
}

export async function createGraphShareCard(options: GraphShareCardOptions) {
  await document.fonts?.ready
  const graphImage = await loadBlobImage(options.graph)
  const canvas = document.createElement('canvas')
  canvas.width = SHARE_CARD_SIZE
  canvas.height = SHARE_CARD_SIZE
  const context = canvas.getContext('2d')
  if (!context) throw new Error('Canvas export is not available in this browser.')

  context.fillStyle = '#08111C'
  context.fillRect(0, 0, SHARE_CARD_SIZE, SHARE_CARD_SIZE)
  drawAtmosphere(context)

  drawBrandMark(context, 78, 74)
  context.textAlign = 'left'
  context.textBaseline = 'alphabetic'
  context.fillStyle = '#F4F0E8'
  context.font = '560 31px "Newsreader Variable", serif'
  context.fillText('MythWeave', 122, 69)
  context.fillStyle = '#D6A647'
  context.font = '700 10px "Noto Sans TC Variable", sans-serif'
  context.letterSpacing = '3px'
  context.fillText('THE HELLENIC ATLAS', 123, 91)
  context.letterSpacing = '0px'

  const titleSize = fitTitle(context, options.title, 700)
  context.fillStyle = '#F4F0E8'
  context.font = `520 ${titleSize}px "Newsreader Variable", "Noto Sans TC Variable", serif`
  context.textAlign = 'right'
  context.fillText(options.title, 1135, 69)
  context.fillStyle = '#9AA7B4'
  context.font = '500 16px "Noto Sans TC Variable", sans-serif'
  context.fillText(options.context, 1135, 95)

  const panel = { x: 64, y: 132, width: 1072, height: 902 }
  context.fillStyle = 'rgba(11,23,36,.92)'
  context.fillRect(panel.x, panel.y, panel.width, panel.height)
  context.strokeStyle = 'rgba(214,166,71,.30)'
  context.lineWidth = 2
  context.strokeRect(panel.x, panel.y, panel.width, panel.height)
  context.fillStyle = '#D6A647'
  context.fillRect(panel.x, panel.y, 102, 4)

  const inset = 18
  const availableWidth = panel.width - inset * 2
  const availableHeight = panel.height - inset * 2
  const imageScale = Math.min(availableWidth / graphImage.width, availableHeight / graphImage.height)
  const imageWidth = graphImage.width * imageScale
  const imageHeight = graphImage.height * imageScale
  const imageX = panel.x + (panel.width - imageWidth) / 2
  const imageY = panel.y + (panel.height - imageHeight) / 2
  context.save()
  context.beginPath()
  context.rect(panel.x + 2, panel.y + 4, panel.width - 4, panel.height - 6)
  context.clip()
  context.drawImage(graphImage, imageX, imageY, imageWidth, imageHeight)
  context.restore()

  const countText = options.locale === 'en'
    ? `${options.nodeCount} mythic items · ${options.edgeCount} relationships`
    : `${options.nodeCount} 個神話項目 · ${options.edgeCount} 條關係`
  context.textAlign = 'left'
  context.fillStyle = '#9AA7B4'
  context.font = '500 16px "Noto Sans TC Variable", sans-serif'
  context.fillText(countText, 64, 1093)
  context.fillStyle = '#7E8C99'
  context.font = '500 12px "Noto Sans TC Variable", sans-serif'
  context.fillText(options.locale === 'en' ? 'Interactive atlas with ancient sources' : '附原典來源的互動神話地圖', 64, 1121)

  context.textAlign = 'right'
  context.fillStyle = '#F0D28C'
  context.font = '600 18px "Noto Sans TC Variable", sans-serif'
  context.fillText('mythweave.cc', 1136, 1094)
  context.fillStyle = '#7E8C99'
  context.font = '500 11px "Noto Sans TC Variable", sans-serif'
  context.fillText(options.locale === 'en' ? 'Portrait and source credits are available in the atlas' : '人物圖像及原典來源說明可在神話地圖查看', 1136, 1121)

  return canvasBlob(canvas)
}

export function buildGraphShareFilename(subject = 'atlas') {
  const safeSubject = subject.toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/^-+|-+$/g, '') || 'atlas'
  return `mythweave-${safeSubject}.png`
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), 1000)
}

export async function shareGraphCard(blob: Blob, filename: string, title: string, stateUrl: string): Promise<GraphShareResult> {
  const file = new File([blob], filename, { type: 'image/png' })
  const data: ShareData = { files: [file], title, text: `${title}\n${stateUrl}` }
  if (typeof navigator.share === 'function' && navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share(data)
      return 'native'
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return 'cancelled'
    }
  }

  downloadBlob(blob, filename)
  return 'download'
}
